import { format, startOfMonth, parse, set, getTime, addHours, isBefore, isEqual, addMonths, addWeeks, parseISO } from 'date-fns'
// Remove the zonedTimeToUtc import since it's causing issues
import type PrestacaoDeConta from '~/types/PrestacaoDeConta'

export const usePrestacaoDeContaStore = defineStore('prestacaoDeContaStore', {
  state: () => ({
    urls: {
      despesas: '/financeiro/despesas',
      receitas: '/financeiro/receitas',
      logsDespesas: '/financeiro/logs/despesas',
      logsReceitas: '/financeiro/logs/receitas',
      logsDocumentos: '/financeiro/logs/documentos',
    },
    despesas: Array<PrestacaoDeConta>(),
    receitas: Array<ReceitasGise>(),
    receitasExtras: Array<Servico>(),
    receitasResumo: { atos: [], extras: [], receitas_gise: [], receitas_bpo: [] },
    categorias: Array<Categoria>(),
    contas: Array<Conta>(),
    modaisAbertos: [] as { modal: any, aoFechar: any | null }[],
    modalLancarDespesa: {
      exibir: false,
      conta_id: '',
      descricao: '',
      valor: 0,
      vencimento: '',
      dataDoPagamento: null,
      observacao: '',
      arquivos: [],
      recorrencias: [],
      criarLembrete: false,
      despesaNaoEfetiva: false,
      despesaEfetiva: true,
    },
    modalLancarReceita: {
      exibir: false,
      servico_id: '',
      quantidade: 0,
      data: new Date().toISOString().slice(0, 10),
      valor: 0,
      servicos: [],
      listaDeServicos: [],
      editMode: false,
      pagador: {},
      favorecido: {},
      estadoForm: {
        passo: 1,
        favorecidoDiferenteDoPagador: false,
        favorecidoCpfCnpj: '',
        favorecidoNome: '',
        pagadorCpfCnpj: '',
        pagadorNome: '',
      },
    },
    modalDetalhesDespesa: {
      exibir: false,
      despesa_id: '',
      despesasRecorrentes: [] as PrestacaoDeConta[],
      cartorioDiferente: false,
      cartorioInfo: null as any,
    },
    modalConfirmaNovaCategoria: {
      exibir: false,
      nome: '',
    },
    modalConfirmaNovaConta: {
      exibir: false,
      nome: '',
      tipo: 'DEDUTIVEL',
    },
    modalEfetuarPagamento: {
      exibir: false,
      despesa_id: '',
      data_pagamento: null,
    },
    modalEditarValorReceita: {
      exibir: false,
      receita: {},
    },
    modalEditarDespesa: {
      exibir: false,
      id: '',
      conta_id: '',
      descricao: '',
      valor: 0,
      vencimento: null,
      dataDoPagamento: null,
      observacao: '',
      arquivos: [],
      criarLembrete: false,
      calendarioId: '',
      eventoCalendario: null,
      despesaNaoEfetiva: false,
      despesaEfetiva: true,
    },
    modalConfirmarExclusao: {
      exibir: false,
      id: '',
      mensagem: '',
    },
    modalConfigurarRecorrencia: {
      exibir: false,
      tipo: 'MENSAL', // ou 'SEMANAL'
      quantidade: 1,
      datas: [] as string[],
      despesaBase: null,
      valor: '',
      confirmado: false, // Adicionar sinalizador de confirmação
      arquivos: [] as File[], // Array para armazenar os arquivos anexados na recorrência
      arquivosIndividuais: {} as Record<number, File[]>, // Arquivos por índice de parcela
      modoEdicaoRecorrencia: false, // Indica se está editando uma recorrência existente
      despesasExistentes: [] as any[], // Lista de despesas já criadas na recorrência
    },
    modalDetalhesReceita: {
      exibir: false,
      transacao: null,
    },
    modalReceitaFixa: {
      exibir: false,
      receita: null as any,
      cartorioDiferente: false,
      cartorioInfo: null as any,
    },
    modalReceitaBpo: {
      exibir: false,
      receita: null as any,
      cartorioDiferente: false,
      cartorioInfo: null as any,
    },
    modalUploadDocumentos: {
      exibir: false,
      arquivos: [] as File[],
      competencia: '',
      documentosExistentes: [] as any[],
    },
    modalConfirmarDespesaDuplicada: {
      exibir: false,
      despesasParaSalvar: [] as any[],
      duplicatasEncontradas: [] as any[],
      callback: null as Function | null,
    },
  }),
  actions: {
    async fetchDespesas ({ competencia = null }: {competencia?: string | null} = {}) {
      let url = this.urls.despesas

      if (!competencia) {
        const currentDate = startOfMonth(new Date())
        competencia = format(currentDate, 'yyyy-MM')
      }

      url += `?competencia=${competencia}`
      const despesas = await useApiStore().request(url) as PrestacaoDeConta[]
      this.despesas = despesas
    },
    async fetchReceitas ({ competencia }: {competencia?: string }) {
      const receitasResumo = await useApiStore().request(this.urls.receitas + `/competencia/${competencia}`, {
        method: 'GET',
      })

      this.receitasResumo = receitasResumo

      if (this.receitasResumo.receitas_bpo) {
        this.receitasResumo.receitas_bpo = this.receitasResumo.receitas_bpo.sort((a, b) => {
          if (a.descricao < b.descricao) {
            return -1
          }
          if (a.descricao > b.descricao) {
            return 1
          }
          return 0
        })
      }

      this.receitas = receitasResumo.receitas_gise
      this.receitasExtras = receitasResumo.extras
    },
    async fetchCategorias () {
      const categorias: Array<Categoria> = await useApiStore().request('/financeiro/categorias') as Array<Categoria>
      this.categorias = categorias
    },
    async fetchContas () {
      const contas: Array<Conta> = await useApiStore().request('/gerencia/conta') as Array<Conta>
      this.contas = contas
    },
    resetarModalLancarDespesa () {
      this.modalLancarDespesa = {
        exibir: false,
        conta_id: '',
        descricao: '',
        valor: 0,
        vencimento: new Date().toISOString().split('T')[0],
        dataDoPagamento: null,
        observacao: '',
        arquivos: [],
        recorrencias: [],
        criarLembrete: false,
        despesaNaoEfetiva: false,
        despesaEfetiva: true,
      }
    },
    prepararModeloDespesa (values: any): Despesa {
      let dados = {
        conta_id: values.conta_id,
        descricao: values.descricao,
        valor: parseFloat(values.valor.toString().replace(',', '.')),
        vencimento: new Date(values.vencimento),
        data_pagamento: values.dataDoPagamento ? new Date(values.dataDoPagamento) : null,
        observacao: values.observacao || null,
        despesa_efetiva: !values.despesaNaoEfetiva,
      }

      // Adicionar campos opcionais se existirem
      if (values.recorrencia_id) {
        dados = { ...dados, recorrencia_id: values.recorrencia_id }
      }
      if (values.numero_parcela) {
        dados = { ...dados, numero_parcela: values.numero_parcela }
      }
      if (values.total_parcelas) {
        dados = { ...dados, total_parcelas: values.total_parcelas }
      }

      return dados
    },
    async salvarDespesa (values: any | any[]) {
      try {
        const despesas = Array.isArray(values) ? values : [values]
        const results = await Promise.all(despesas.map(async (despesa) => {
          const arquivos = []
          if (despesa.arquivos?.length > 0) {
            arquivos.push(despesa.arquivos)
          }

          const despesaData = {
            ...this.prepararModeloDespesa(despesa),
            arquivos,
          }

          const formData = new FormData()
          formData.append('despesa', JSON.stringify(despesaData))

          if (despesa.arquivos && despesa.arquivos.length > 0) {
            despesa.arquivos.forEach((arquivo) => {
              formData.append('arquivos[]', arquivo)
            })
          }

          const response = await useApiStore().upload(this.urls.despesas, formData)
          if (!response.id) {
            throw new Error('Erro ao salvar despesa')
          }
          if (despesa.criarLembrete) {
            try {
              await this.criarLembreteCalendario(response)
            } catch (error: any) {
              if (error.status === 403 || error.message.includes('não tem permissão')) {
                useStore().toasts.push({
                  content: error.message || 'Você não tem permissão para criar eventos neste calendário',
                  color: 'warning',
                  // timeout: 8000 // Give more time to read the message
                })
              } else {
                useStore().toasts.push({
                  content: 'Despesa salva, mas não foi possível criar o lembrete no calendário',
                  color: 'warning',
                  // timeout: 8000
                })
              }
            }
          }

          return response
        }))

        useStore().toasts.push({
          content: `Despesa${despesas.length > 1 ? 's' : ''} salva${despesas.length > 1 ? 's' : ''} com sucesso`,
          color: 'success',
        })

        return results.length === 1 ? results[0] : results
      } catch (error) {
        console.error(error)
        useStore().toasts.push({
          content: error.message || 'Erro ao salvar despesa',
          color: 'danger',
        })
        throw error
      } finally {
        useStore().loading = false
      }
    },
    async criarLembreteCalendario (despesa: any) {
      try {
        // Parse the vencimento date and set it to 7am
        const startTime = parse(despesa.vencimento, 'yyyy-MM-dd', new Date())
        const reminderTime = set(startTime, { hours: 7, minutes: 0, seconds: 0, milliseconds: 0 })

        // Add 3 hours to compensate for timezone (assuming UTC-3)
        const adjustedTime = addHours(reminderTime, 3)

        const calendarData = {
          title: `Vencimento: ${despesa.descricao}`,
          description: `Valor: R$ ${despesa.valor}\nObservação: ${despesa.observacao || 'Nenhuma'}`,
          start_time: getTime(adjustedTime),
          end_time: getTime(adjustedTime),
        }

        const larkStore = useLarkStore()

        if (!larkStore.calendarId) {
          throw new Error('Nenhum calendário padrão configurado')
        }

        await larkStore.createCalendarEvent(larkStore.calendarId, calendarData, despesa)
      } catch (error) {
        console.error('Erro ao criar lembrete:', error.message)
        useStore().toasts.push({
          content: 'Erro ao criar lembrete: ' + error.message,
          color: 'warning',
        })
      }
    },
    async salvarCategoria (nome: string) {
      try {
        const categoria = await useApiStore().request('/financeiro/categoria', {
          method: 'POST',
          body: { nome },
        })
        return categoria
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    resetarModalAdicionarCategoria () {
      this.modalConfirmaNovaCategoria = {
        exibir: false,
        nome: '',
      }
    },
    salvarConta (nome: string, tipo: string) {
      try {
        return useApiStore().request('/gerencia/conta', {
          method: 'POST',
          body: { nome, tipo },
        })
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    resetarModalAdicionarConta () {
      this.modalConfirmaNovaConta = {
        exibir: false,
        nome: '',
        tipo: 'DEDUTIVEL',
      }
    },
    resetarModalEfetuarPagamento () {
      this.modalEfetuarPagamento = {
        exibir: false,
        despesa_id: '',
        data_pagamento: null,
      }
    },
    async efetuarPagamento () {
      try {
        const pagamento = await useApiStore().request(`/financeiro/despesas/${this.modalEfetuarPagamento.despesa_id}/pagar`, {
          method: 'POST',
          body: { data_pagamento: this.modalEfetuarPagamento.data_pagamento },
        })
        return pagamento
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    resetarModalLancarReceita () {
      this.modalLancarReceita = {
        exibir: false,
        servico_id: '',
        data: new Date().toISOString().slice(0, 10),
        quantidade: 0,
        valor: 0,
        pagador: {},
        favorecido: {},
        servicos: [],
        listaDeServicos: this.modalLancarReceita.listaDeServicos,
        editMode: false,
        estadoForm: {
          passo: 1,
          favorecidoDiferenteDoPagador: false,
          favorecidoCpfCnpj: '',
          favorecidoNome: '',
          pagadorCpfCnpj: '',
          pagadorNome: '',
        },
      }
    },
    validarCpfCnpj (values: any) {
      if (!values.favorecidoDiferenteDoPagador) {
        values.favorecidoCpfCnpj = values.pagadorCpfCnpj
        values.favorecidoNome = values.pagadorNome
      }
    },
    async adicionarReceita () {
      try {
        await console.log('add receita')
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    adicionarPagamentoNaReceita (pagamento: any) {
      try {
        let novoPagamentoData
        if (Array.isArray(pagamento)) {
          novoPagamentoData = pagamento
        } else {
          pagamento.dataBase = this.modalLancarReceita.data
          novoPagamentoData = [useOrcamentoStore().prepararModeloNovoPagamento(pagamento)]
        }
        useOrcamentoStore().orcamento.pagamentos = [...useOrcamentoStore().orcamento.pagamentos ?? [], ...novoPagamentoData]
        // const pagamentoData = useOrcamentoStore().prepararModeloPagamento(novoPagamentoData)
        return true
      } catch (err) {
        return err
      }
    },
    resetarModalEditarValorReceita () {
      this.modalEditarValorReceita = {
        exibir: false,
        receita: {},
      }
    },
    async editarValorReceita (receita) {
      try {
        let valor = parseFloat(receita.valor.replace('.', '').replace(',', '.'))
        if (valor > 0 && receita.descricao.toUpperCase() === 'Imposto de Renda Retido na Fonte'.toUpperCase()) {
          valor = valor * -1
        }
        await useApiStore().request(`/financeiro/receitas/${receita.id}`, {
          method: 'PUT',
          body: { valor },
        })

        return true
      } catch (error) {
        console.error(error)
        return false
      }
    },
    prepararModeloNovaReceita (): BpoNovaReceita {
      const model = {
        valor: this.getTotalDosServicos,
        descricao: '',
        servicos: this.modalLancarReceita.servicos,
        pagamentos: useOrcamentoStore().orcamento.pagamentos,
        pagador_pessoa_id: this.modalLancarReceita.pagador.id,
        favorecido_pessoa_id: this.modalLancarReceita.favorecido.id,
        tipo: 'UNICA',
      }

      return model
    },
    async salvarReceita () {
      try {
        const novaReceita = this.prepararModeloNovaReceita()
        const response = await useApiStore().request(this.urls.receitas, {
          method: 'POST',
          body: novaReceita,
        })

        return response
      } catch (error) {
        console.error(error)
        return false
      }
    },
    validarPagadorEFavorecidoReceita () {
      if (!this.modalLancarReceita.pagador?.id) {
        useStore().toasts.push({
          content: 'Pagador não selecionado',
          color: 'danger',
        })
        return false
      }
      if (this.modalLancarReceita.estadoForm.favorecidoDiferenteDoPagador && !this.modalLancarReceita.favorecido?.id) {
        useStore().toasts.push({
          content: 'Favorecido não selecionado',
          color: 'danger',
        })
        return false
      }
      if (!this.modalLancarReceita.favorecido?.id) {
        this.modalLancarReceita.favorecido = this.modalLancarReceita.pagador
        return false
      }
      return true
    },
    validarSePagamentosEstaoPreenchidos () {
      if (this.getTotalDosServicos > this.getTotalDosPagamentos) {
        useStore().toasts.push({
          content: 'Valor pago menor que o valor total dos serviços',
          color: 'danger',
        })
        return false
      }
      if (this.getTotalDosServicos < this.getTotalDosPagamentos) {
        useStore().toasts.push({
          content: 'Valor pago maior que o valor total dos serviços',
          color: 'danger',
        })
        return false
      }
      return true
    },
    async lancarReceita () {
      if (this.validarPagadorEFavorecidoReceita() && this.validarSePagamentosEstaoPreenchidos()) {
        return await this.salvarReceita()
      }
    },
    resetarModalEditarDespesa () {
      this.modalEditarDespesa = {
        exibir: false,
        id: '',
        conta_id: '',
        descricao: '',
        valor: 0,
        vencimento: null,
        dataDoPagamento: null,
        observacao: '',
        arquivos: [],
        criarLembrete: false,
        calendarioId: '',
        eventoCalendario: null,
        despesaNaoEfetiva: false,
        despesaEfetiva: true,
      }
    },
    async editarDespesa (id: string, formData: FormData) {
      try {
        const response = await useApiStore().upload(
          `${this.urls.despesas}/${id}`,
          formData
        )

        if (!response.id) {
          throw new Error('Erro ao atualizar despesa')
        }

        const despesaData = JSON.parse(formData.get('despesa') as string)
        if (despesaData.criarLembrete) {
          try {
            await this.criarLembreteCalendario(despesaData)
          } catch (error: any) {
            if (error.status === 403 || error.message.includes('não tem permissão')) {
              useStore().toasts.push({
                content: error.message || 'Você não tem permissão para criar eventos neste calendário',
                color: 'warning',
              })
            } else {
              useStore().toasts.push({
                content: 'Despesa atualizada, mas não foi possível criar o lembrete no calendário',
                color: 'warning',
              })
            }
          }
        }

        const competencia = useStore().competenciaFormatada
        await this.fetchDespesas({ competencia })

        return response
      } catch (error) {
        console.error('Erro na store ao atualizar despesa:', error)
        throw error
      }
    },
    async excluirDespesa (id: string, refresh: boolean = true) {
      try {
        const despesa = this.despesas.find(d => d.id === id)
        await useApiStore().request(`${this.urls.despesas}/${id}`, {
          method: 'DELETE',
        })
        let competencia
        if (refresh) {
          competencia = format(new Date(despesa.vencimento), 'yyyy-MM')
        }
        await this.fetchDespesas({ competencia })

        return true
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async verificarDespesaDuplicada (dados: {
      conta_id: string
      valor: number
      vencimento: string
      despesa_id?: string
    }) {
      try {
        const response = await useApiStore().request(
          `${this.urls.despesas}/verificar-duplicada`,
          {
            method: 'POST',
            body: dados,
          }
        )
        return response
      } catch (error) {
        console.error('Erro ao verificar despesa duplicada:', error)
        throw error
      }
    },
    async deletarEventoCalendario (eventoId: string) {
      try {
        await useApiStore().request(`/financeiro/despesas/evento/${eventoId}`, {
          method: 'DELETE',
        })
        useStore().toasts.push({ content: 'Evento de calendário excluído com sucesso', color: 'success' })
      } catch (error) {
        console.error('Erro ao excluir evento de calendário:', error)
        useStore().toasts.push({ content: 'Erro ao excluir evento de calendário', color: 'danger' })
      }
    },
    resetarModalConfigurarRecorrencia () {
      this.modalConfigurarRecorrencia = {
        exibir: false,
        tipo: 'MENSAL',
        quantidade: 1,
        datas: [],
        despesaBase: null,
        valor: '',
        confirmado: false,
        arquivos: [],
        arquivosIndividuais: {},
        modoEdicaoRecorrencia: false,
        despesasExistentes: [],
      }
    },
    calcularDatasRecorrencia () {
      if (!this.modalConfigurarRecorrencia.despesaBase?.vencimento) {
        this.modalConfigurarRecorrencia.datas = []
        return
      }

      const datas = []
      let dataBase

      // Se em modo de edição, calcular a partir da última parcela existente
      if (this.modalConfigurarRecorrencia.modoEdicaoRecorrencia &&
          this.modalConfigurarRecorrencia.despesasExistentes.length > 0) {
        // Pegar a última data das despesas existentes
        const ultimaDespesa = this.modalConfigurarRecorrencia.despesasExistentes
          .sort((a, b) => new Date(b.vencimento).getTime() - new Date(a.vencimento).getTime())[0]
        dataBase = parseISO(ultimaDespesa.vencimento)
      } else {
        // Modo normal: usar a data base da despesa
        const despesa = this.modalConfigurarRecorrencia.despesaBase
        dataBase = parseISO((despesa as any).vencimento)
        // Adicionar primeira data no modo normal
        datas.push(format(dataBase, 'yyyy-MM-dd'))
      }

      // Gerar próximas datas
      const startIndex = this.modalConfigurarRecorrencia.modoEdicaoRecorrencia ? 1 : 1
      const endIndex = this.modalConfigurarRecorrencia.modoEdicaoRecorrencia
        ? this.modalConfigurarRecorrencia.quantidade + 1
        : this.modalConfigurarRecorrencia.quantidade
      for (let i = startIndex; i < endIndex; i++) {
        let novaData

        if (this.modalConfigurarRecorrencia.tipo === 'MENSAL') {
          // Usar addMonths do date-fns que já trata casos especiais
          novaData = addMonths(dataBase, i)
        } else {
          // Usar addWeeks do date-fns
          novaData = addWeeks(dataBase, i)
        }

        datas.push(format(novaData, 'yyyy-MM-dd'))
      }

      this.modalConfigurarRecorrencia.datas = datas
    },
    prepararDespesasRecorrentes () {
      const despesaBase = this.modalConfigurarRecorrencia.despesaBase
      return this.modalConfigurarRecorrencia.datas.map(data => ({
        ...despesaBase,
        vencimento: data,
      }))
    },
    async downloadComprovantes (despesa) {
      const despesaId = despesa.id
      const nomeDoArquivo = despesa.contaObj?.nome ?? despesa.conta?.nome
      const mesDaDespesa = format(new Date(despesa.vencimento ?? despesa.data), 'MM')
      const anoDaDespesa = format(new Date(despesa.vencimento ?? despesa.data), 'yyyy')
      const nomeDoArquivoSanitizado = decodeURIComponent(nomeDoArquivo.replace(/ /g, '_')).normalize('NFD').replace(/[\u0300-\u036F]/g, '').replace(/[^a-zA-Z0-9\s]/g, '_')

      const response = await useApiStore().request(`/financeiro/despesas/${despesaId}/comprovantes`, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf', // Assegure que o tipo de conteúdo está correto
        },
      })

      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `comprovantes_${nomeDoArquivoSanitizado}_${mesDaDespesa}_${anoDaDespesa}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    async excluirComprovante (despesaId, comprovanteId) {
      try {
        await useApiStore().request(
          `/financeiro/despesas/${despesaId}/comprovantes/${comprovanteId}`,
          { method: 'DELETE' }
        )
      } catch (error) {
        console.error('Erro ao excluir comprovante:', error)
        useStore().toasts.push({ content: 'Erro ao excluir comprovante', color: 'danger' })
      }
    },
    async downloadArquivoMescladoAcervo (pcAnexo) {
      const response = await useApiStore().request(`/gerencia/download/acervo/${pcAnexo.acervo.id}`, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf', // Assegure que o tipo de conteúdo está correto
        },
      })

      const link = document.createElement('a')
      link.href = response
      link.target = '_blank'
      link.setAttribute('download', pcAnexo.titulo + '.pdf')
      document.body.appendChild(link)
      link.click()
      link.remove()
    },
    resetarModalDetalhesReceita () {
      this.modalDetalhesReceita = {
        exibir: false,
        transacao: null,
      }
    },
    async abrirModalReceitaPorId (receitaId: string) {
      try {
        // Carrega os dados da receita via API usando endpoint específico para modal
        const receita = await useApiStore().request(`financeiro/receitas/modal/${receitaId}`) as any

        // Verifica se é uma receita FIXA e abre o modal apropriado
        if (receita.tipo === 'FIXA') {
          this.modalReceitaFixa = {
            exibir: true,
            receita,
            cartorioDiferente: receita?.cartorio_diferente || false,
            cartorioInfo: receita?.cartorio_info || null,
          }
        } else {
          // Para receitas BPO não-fixas (UNICA, etc), abre modal detalhado
          this.modalReceitaBpo = {
            exibir: true,
            receita,
            cartorioDiferente: receita?.cartorio_diferente || false,
            cartorioInfo: receita?.cartorio_info || null,
          }
        }
      } catch (error) {
        console.error('Erro ao carregar receita:', error)
        throw error
      }
    },
    resetarModalReceitaFixa () {
      this.modalReceitaFixa = {
        exibir: false,
        receita: null,
        cartorioDiferente: false,
        cartorioInfo: null,
      }
    },
    resetarModalReceitaBpo () {
      this.modalReceitaBpo = {
        exibir: false,
        receita: null,
        cartorioDiferente: false,
        cartorioInfo: null,
      }
    },
    async downloadComprovante (despesaId: string, comprovanteId: string) {
      try {
        const url = await useApiStore().request(
          `/financeiro/despesas/${despesaId}/comprovantes/${comprovanteId}`,
          { method: 'GET' }
        )
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.click()
        link.remove()
      } catch (error) {
        console.error('Erro ao obter comprovante:', error)
        useStore().toasts.push({ content: 'Erro ao obter comprovante', color: 'danger' })
      }
    },
    converterTransacaoParaBpoDespesa (transacao: any) {
      return {
        id: transacao.id,
        conta_id: transacao.contaObj.id,
        conta: transacao.contaObj,
        descricao: transacao.historico,
        valor: transacao.valor,
        vencimento: transacao.data,
        dataDoPagamento: null,
        observacao: '',
        arquivos: transacao.comprovantes,
        criarLembrete: false,
        recorrencias: [],
      }
    },
    async buscarDespesasRecorrentes (recorrenciaId: string) {
      try {
        const despesas = await useApiStore().request(`${this.urls.despesas}/recorrentes/${recorrenciaId}`) as PrestacaoDeConta[]
        return despesas
      } catch (error) {
        console.error('Erro ao buscar despesas recorrentes:', error)
        throw error
      }
    },
    async abrirModalDetalhesDespesa (despesaId: string) {
      try {
        // Primeiro tenta encontrar a despesa no store
        let despesa = this.despesas.find(d => d.id === despesaId)

        // Se não encontrar, carrega via API usando endpoint específico para modal
        if (!despesa) {
          despesa = await useApiStore().request(`financeiro/despesas/modal/${despesaId}`) as any

          // Adiciona temporariamente ao store para uso futuro
          if (despesa && !this.despesas.find(d => d.id === despesa.id)) {
            this.despesas.push(despesa)
          }
        } else {
          // Se encontrou no store, ainda precisa verificar se é de cartório diferente
          // Usa endpoint modal para obter informações completas
          const despesaCompleta = await useApiStore().request(`financeiro/despesas/modal/${despesaId}`) as any

          // Atualiza com informações de cartório se necessário
          if (despesaCompleta.cartorio_diferente) {
            despesa.cartorio_diferente = despesaCompleta.cartorio_diferente
            despesa.cartorio_info = despesaCompleta.cartorio_info
          }
        }

        // Configura estado do modal
        this.modalDetalhesDespesa.despesa_id = despesaId
        this.modalDetalhesDespesa.cartorioDiferente = despesa?.cartorio_diferente || false
        this.modalDetalhesDespesa.cartorioInfo = despesa?.cartorio_info || null

        // Carrega despesas recorrentes se necessário
        if (despesa?.recorrencia_id) {
          try {
            this.modalDetalhesDespesa.despesasRecorrentes = await this.buscarDespesasRecorrentes(despesa.recorrencia_id)
          } catch (error) {
            console.error('Erro ao buscar despesas recorrentes:', error)
          }
        }

        // Só abre o modal APÓS garantir que a despesa foi carregada
        this.modalDetalhesDespesa.exibir = true
      } catch (error) {
        console.error('Erro ao carregar despesa:', error)
        throw error
      }
    },
    resetarModalDetalhesDespesa () {
      this.modalDetalhesDespesa = {
        exibir: false,
        despesa_id: '',
        despesasRecorrentes: [],
        cartorioDiferente: false,
        cartorioInfo: null,
      }
    },

    // Funções para logs
    async buscarLogsDespesa (despesaId: string) {
      try {
        const logs = await useApiStore().request(`${this.urls.logsDespesas}/${despesaId}`)
        return logs
      } catch (error) {
        console.error('Erro ao buscar logs da despesa:', error)
        return []
      }
    },

    async buscarLogsReceita (receitaId: string) {
      try {
        const logs = await useApiStore().request(`${this.urls.logsReceitas}/${receitaId}`)
        return logs
      } catch (error) {
        console.error('Erro ao buscar logs da receita:', error)
        return []
      }
    },

    async buscarLogsDocumentos (competencia: string) {
      try {
        const logs = await useApiStore().request(`${this.urls.logsDocumentos}/${competencia}`)
        return logs
      } catch (error) {
        console.error('Erro ao buscar logs de documentos:', error)
        return []
      }
    },

    async buscarLogsCartorio (params: { tipo: 'despesas' | 'receitas', dataInicio?: string, dataFim?: string, limite?: number } = { tipo: 'despesas' }) {
      try {
        const baseUrl = params.tipo === 'despesas' ? this.urls.logsDespesas : this.urls.logsReceitas
        const query = new URLSearchParams()

        if (params.dataInicio) { query.append('data_inicio', params.dataInicio) }
        if (params.dataFim) { query.append('data_fim', params.dataFim) }
        if (params.limite) { query.append('limite', params.limite.toString()) }

        const url = `${baseUrl}/cartorio${query.toString() ? '?' + query.toString() : ''}`
        const logs = await useApiStore().request(url)
        return logs
      } catch (error) {
        console.error('Erro ao buscar logs do cartório:', error)
        return []
      }
    },

    async buscarLogsUsuario (tipo: 'despesas' | 'receitas' = 'despesas', limite: number = 50) {
      try {
        const response = await useApiStore().request(`${this.urls[tipo === 'despesas' ? 'logsDespesas' : 'logsReceitas']}/usuario`, {
          method: 'GET',
          params: { limite },
        })
        return response
      } catch (error) {
        console.error('Erro ao buscar logs do usuário:', error)
        throw error
      }
    },

    // Ações para gerenciar documentos por competência
    async buscarDocumentosCompetencia (competencia: string) {
      try {
        const response = await useApiStore().request(`/financeiro/documentos/${competencia}`)
        return response || []
      } catch (error) {
        console.error('Erro ao buscar documentos:', error)
        return []
      }
    },

    async salvarDocumentosCompetencia (competencia: string, arquivos: File[]) {
      try {
        const formData = new FormData()
        formData.append('competencia', competencia)

        arquivos.forEach((arquivo, index) => {
          formData.append(`documentos[${index}]`, arquivo)
        })

        const response = await useApiStore().upload('/financeiro/documentos', formData)

        useStore().toasts.push({
          content: 'Documentos enviados com sucesso',
          color: 'success',
        })

        return response
      } catch (error) {
        console.error('Erro ao salvar documentos:', error)
        useStore().toasts.push({
          content: 'Erro ao enviar documentos',
          color: 'danger',
        })
        throw error
      }
    },

    async excluirDocumentoCompetencia (documentoId: string) {
      try {
        await useApiStore().request(`/financeiro/documentos/${documentoId}`, {
          method: 'DELETE',
        })

        useStore().toasts.push({
          content: 'Documento excluído com sucesso',
          color: 'success',
        })
      } catch (error) {
        console.error('Erro ao excluir documento:', error)
        useStore().toasts.push({
          content: 'Erro ao excluir documento',
          color: 'danger',
        })
        throw error
      }
    },

    abrirModalUploadDocumentos () {
      this.modalUploadDocumentos.competencia = useStore().competenciaFormatada
      this.modalUploadDocumentos.exibir = true
      this.buscarDocumentosCompetencia(this.modalUploadDocumentos.competencia).then((documentos) => {
        this.modalUploadDocumentos.documentosExistentes = documentos as any
      })
    },

    resetarModalUploadDocumentos () {
      this.modalUploadDocumentos.exibir = false
      this.modalUploadDocumentos.arquivos = []
      this.modalUploadDocumentos.competencia = ''
      this.modalUploadDocumentos.documentosExistentes = []
    },

    resetarModalConfirmarDespesaDuplicada () {
      this.modalConfirmarDespesaDuplicada = {
        exibir: false,
        despesasParaSalvar: [],
        duplicatasEncontradas: [],
        callback: null,
      }
    },

    async verificarTodasDespesasDuplicadas (despesas: any[]) {
      try {
        const duplicatasPorDespesa = []

        for (let i = 0; i < despesas.length; i++) {
          const despesa = despesas[i]
          const dadosVerificacao = {
            conta_id: despesa.conta_id,
            valor: typeof despesa.valor === 'string'
              ? parseFloat(despesa.valor.toString().replace('.', '').replace(',', '.'))
              : despesa.valor,
            vencimento: despesa.vencimento,
            despesa_id: despesa.id || null,
          }

          const resultado = await this.verificarDespesaDuplicada(dadosVerificacao) as any

          if (resultado.tem_duplicadas) {
            duplicatasPorDespesa.push({
              despesaIndex: i,
              despesa,
              duplicatas: resultado.despesas_similares,
            })
          }
        }

        return duplicatasPorDespesa
      } catch (error) {
        console.error('Erro ao verificar despesas duplicadas:', error)
        throw error
      }
    },
    // Download de BpoDocumentos mesclados por competência
    async baixarDocumentoCompetencia (documento: any) {
      try {
        const blob = await useApiStore().download(`/financeiro/documentos/${documento.id}/download`)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', documento.nome_original || 'documento.pdf')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Erro ao baixar documento:', error)
        useStore().toasts.push({
          content: 'Erro ao baixar documento',
          color: 'danger',
        })
      }
    },

    // Visualizar documento em nova janela
    async visualizarDocumentoCompetencia (documento: any) {
      try {
        const response = await useApiStore().request(`/financeiro/documentos/${documento.id}/link`)
        if (response.url) {
          window.open(response.url, '_blank')
        } else {
          throw new Error('Link não disponível')
        }
      } catch (error) {
        console.error('Erro ao obter link do documento:', error)
        useStore().toasts.push({
          content: 'Erro ao visualizar documento',
          color: 'danger',
        })
      }
    },
  },
  getters: {
    isCompetenciaAberta: () => {
      try {
        const competencia = useStore().competencia
        const hoje = new Date()

        // Criar data da competência (primeiro dia do mês)
        const dataCompetencia = new Date(competencia.ano, competencia.mes - 1, 1)
        const dataAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1)

        // Competência é aberta se for mês atual ou futuro
        return isEqual(dataCompetencia, dataAtual) || !isBefore(dataCompetencia, dataAtual)
      } catch (error) {
        // Em caso de erro, considera como aberta para não bloquear
        console.warn('Erro ao verificar competência aberta:', error)
        return true
      }
    },
    getTotalDespesas (state) {
      return state.despesas?.filter(despesa => despesa.conta?.tipo.toLowerCase() === 'dedutivel').reduce((acc, despesa) => {
        return parseFloat(despesa.valor.toString().replace(',', '.')) + acc
      }, 0)
      // return state.despesas?.reduce((acc, despesa) => parseFloat((despesa.valor - despesa.tfj - despesa.iss - despesa.funcivil - despesa.fundo_eletronizacao).toString().replace(',', '.')) + acc, 0)
    },
    getTotalReceitas (state) {
      const totalReceitas = state.receitas.reduce((acc, receita) => parseFloat(receita.emolumento.toString().replace(',', '.')) + acc, 0)
      const totalReceitasExtras = state.receitasExtras.reduce((acc, receita) => parseFloat(receita.valor.toString().replace(',', '.')) + acc, 0)
      const totalReceitasBpo = state.receitasResumo.receitas_bpo.reduce((acc, receita) => parseFloat(receita.valor.replace(',', '.')) + acc, 0)
      return totalReceitas + totalReceitasExtras + totalReceitasBpo
    },
    getTotalDespesasNaoDedutiveis (state) {
      return state.despesas?.filter(despesa => despesa.conta?.tipo.toLowerCase() !== 'dedutivel').reduce((acc, despesa) => {
        return parseFloat(despesa.valor.toString().replace(',', '.')) + acc
      }, 0)
    },
    getTotalRepasses (state) {
      return state.receitas.reduce((acc, receita) => {
        return (parseFloat(receita.tfj) + parseFloat(receita.funcivil) + parseFloat(receita.fse ?? 0)) + acc
      }, 0) + this.getTotalReceitas * (parseFloat(useUsuarioStore().usuarioAtual.cartorio.iss))
    },
    getPagamentosComParcelas: (state) => {
      let pagamentosAgrupados = []

      for (let i = 0; i < state.orcamento.pagamentos?.length; i++) {
        const pagamento = state.orcamento.pagamentos[i]
        const formaDePagamento = pagamento.formaPagamento ?? pagamento.forma
        const quantidadeDeParcelas = pagamento.quantidadeParcelas ?? pagamento.parcelas
        if (formaDePagamento !== 'CARTAO_DE_CREDITO') {
          pagamentosAgrupados[i] = pagamento
        } else if (quantidadeDeParcelas === 1) {
          pagamentosAgrupados[i] = { ...pagamento, children: [] }
          let j = i
          for (j; j < state.orcamento.pagamentos?.length; j++) {
            const proximoPagamento = state.orcamento.pagamentos[j]
            if (([proximoPagamento.forma, proximoPagamento.formaPagamento].includes('CARTAO_DE_CREDITO') && (proximoPagamento.quantidadeParcelas ?? proximoPagamento.parcelas) > 1)) {
              pagamentosAgrupados[i].children.push(proximoPagamento)
              if ((state.orcamento.pagamentos[j + 1]?.quantidadeParcelas ?? state.orcamento.pagamentos[j + 1]?.parcelas) === 1) {
                i = j
                break
              }
            }
          }
        }
      }
      pagamentosAgrupados = pagamentosAgrupados.filter((pagamento) => {
        return pagamento !== undefined
      })
      return pagamentosAgrupados
    },
    getTotalDosServicos: (state) => {
      return state.modalLancarReceita.servicos?.reduce((acc, servico) => acc + (servico.valor * servico.quantidade), 0) ?? 0
    },
    getTotalDosPagamentos: () => {
      return useOrcamentoStore().orcamento.pagamentos?.reduce((acc, pagamento) => acc + pagamento.valor, 0) ?? 0
    },
    getTemPermissaoExcluir: () => {
      try {
        // Verificação base de permissão
        const temPermissaoBase = useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

        if (!temPermissaoBase) { return false }

        // Admin sempre pode excluir
        const usuario = useUsuarioStore().usuarioAtual.usuario
        const isAdmin = usuario?.admin === 'S'
        if (isAdmin) { return true }

        // Usuários com permissão de exclusão não são afetados pela trava
        if (temPermissaoBase) { return true }

        // Para outros usuários, verificar trava
        const permissaoStore = usePermissaoStore()
        console.log('Trava permitir edição:', permissaoStore.permitirEdicaoCompetenciaFechada)

        if (permissaoStore.permitirEdicaoCompetenciaFechada) { return true }

        return usePrestacaoDeContaStore().isCompetenciaAberta
      } catch (error) {
        console.warn('Erro na verificação de permissão excluir:', error)
        return useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')
      }
    },
    getTemPermissaoEditar: () => {
      try {
        // Verificação base de permissão
        const temPermissaoBase = useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.editar === 1 && permissao.especialidade_nome === 'financeiro')

        if (!temPermissaoBase) { return false }

        // Admin sempre pode editar
        const usuario = useUsuarioStore().usuarioAtual.usuario
        const isAdmin = usuario?.admin === 'S'
        if (isAdmin) { return true }

        // Usuários com permissão de exclusão não são afetados pela trava
        const temPermissaoExcluir = useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

        if (temPermissaoExcluir) { return true }

        // Para outros usuários, verificar trava
        const permissaoStore = usePermissaoStore()
        console.log('Trava permitir edição:', permissaoStore.permitirEdicaoCompetenciaFechada)

        if (permissaoStore.permitirEdicaoCompetenciaFechada) { return true }

        return usePrestacaoDeContaStore().isCompetenciaAberta
      } catch (error) {
        console.warn('Erro na verificação de permissão editar:', error)
        return useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.editar === 1 && permissao.especialidade_nome === 'financeiro')
      }
    },
    getTemPermissaoCriar: () => {
      try {
        // Verificação base de permissão
        const temPermissaoBase = useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.inserir === 1 && permissao.especialidade_nome === 'financeiro')

        if (!temPermissaoBase) { return false }

        // Admin sempre pode criar
        const usuario = useUsuarioStore().usuarioAtual.usuario
        const isAdmin = usuario?.admin === 'S'
        if (isAdmin) { return true }

        // Usuários com permissão de exclusão não são afetados pela trava
        const temPermissaoExcluir = useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

        if (temPermissaoExcluir) { return true }

        // Para outros usuários, verificar trava
        const permissaoStore = usePermissaoStore()
        console.log('Trava permitir edição:', permissaoStore.permitirEdicaoCompetenciaFechada)
        console.log('Competência aberta:', usePrestacaoDeContaStore().isCompetenciaAberta)

        if (permissaoStore.permitirEdicaoCompetenciaFechada) { return true }

        return usePrestacaoDeContaStore().isCompetenciaAberta
      } catch (error) {
        console.warn('Erro na verificação de permissão criar:', error)
        return useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.inserir === 1 && permissao.especialidade_nome === 'financeiro')
      }
    },
    getTemPermissaoPagar: () => {
      try {
        // Verificação base de permissão
        const temPermissaoBase = useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.editar === 1 && permissao.especialidade_nome === 'financeiro')

        if (!temPermissaoBase) { return false }

        // Admin sempre pode pagar
        const usuario = useUsuarioStore().usuarioAtual.usuario
        const isAdmin = usuario?.admin === 'S'
        if (isAdmin) { return true }

        // Usuários com permissão de exclusão não são afetados pela trava
        const temPermissaoExcluir = useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

        if (temPermissaoExcluir) { return true }

        // Para outros usuários, verificar trava
        const permissaoStore = usePermissaoStore()
        if (permissaoStore.permitirEdicaoCompetenciaFechada) { return true }

        return usePrestacaoDeContaStore().isCompetenciaAberta
      } catch (error) {
        console.warn('Erro na verificação de permissão pagar:', error)
        return useUsuarioStore().usuarioAtual.cartorio.permissoes
          .some(permissao => permissao.editar === 1 && permissao.especialidade_nome === 'financeiro')
      }
    },
    contasAgrupadasPorCategoria: (state) => {
      const categorias = state.contas?.reduce((acc: string[], conta: { categoria: string }) => {
        if (!acc.includes(conta.categoria)) {
          acc.push(conta.categoria)
        }
        return acc
      }, [])

      const contasAgrupadas = categorias.map((categoria) => {
        return {
          label: categoria,
          options: state.contas.filter(conta => conta.categoria === categoria).map(conta => ({ value: conta.id, label: conta.nome })),
        }
      })

      return contasAgrupadas
    },
  },

  persist: {
    paths: [],
  },
})
